import { entryMessage } from "./useEntryOperation"

// Why the API's admission rules refuse a participant or a team (Events entries/eligibility.js and levelRules.js).
// A refusal does not change on retry: only another selection, or another competition, can fit.
// quoteRefusal below adds the other quote refusals that name a person or a fix (Events entryManagement/service.js quote()).
const restrictionText = {
  FORBIDDEN_AGE: "is under 15, the minimum age for Sportya competitions.",
  GENDER: "does not match this competition's gender restriction.",
  MINAGE: "is younger than this competition's minimum age.",
  MAXAGE: "is older than this competition's maximum age.",
  RANK: "does not meet this competition's rank restriction.",
  ACCOUNTTYPE: "does not have the account plan this competition requires.",
  ACCOUNTACTIVITY: "does not meet this competition's account activity restriction.",
}

export const isLevel = value => typeof value === "number" && Number.isFinite(value)

const fullName = (person, fallback) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || fallback

// The same wording as the API's level check in the competition editor (competitionEditor/levelChange.js).
const levelRange = (level, gameType, restrictions = {}) => gameType === "doubles"
  ? `Level ${level} accepts teams averaging ${level - 1} to ${restrictions.teamAverageLevel ? level - 0.5 : level}, with partners at most 2 levels apart${
    restrictions.forbiddenToTeams ? ` and no player above level ${level}` : ""}.`
  : `Level ${level} accepts players of level ${level - 1} or ${level}.`

// A person the search read without a level for `game`: their gameLevel is an object (the legacy search's {}, or the published
// ranking's nulls) with no number for it. A person the search sent no gameLevel for is not known to lack one.
const lacksLevel = (person, game) => Boolean(person?.gameLevel) && typeof person.gameLevel === "object" && !isLevel(person.gameLevel[game])

// Only another selection, or another competition, fits.
const refused = text => ({ text, recheck: false })

// After Check again (`levelsRechecked`), the levels the search sent with the pick may be out of date: the admin may have set one
// in a player profile meanwhile. A level refusal then names no level, and Check again stays.
const teamLevel = ({ level, restrictions, player, partner, levelsRechecked }) => {
  if (levelsRechecked) return { recheck: true, text: "This team does not fit this competition's level restrictions." }
  // When both lack one, both are named: Events would read the one left unset as 0 too (levelRules.js), so naming only the
  // payer sends the admin back to the partner's profile after Check again.
  const unset = [[player, "The player"], [partner, "The partner"]].filter(([person]) => lacksLevel(person, "doubles"))
  if (unset.length === 2) {
    return { recheck: true,
      text: `${fullName(player, "The player")} and ${fullName(partner, "the partner")} have no doubles level set, so this team cannot be admitted. Set it in their player profiles, then check again.` }
  }
  if (unset.length) {
    return { recheck: true, text: `${fullName(...unset[0])} has no doubles level set, so this team cannot be admitted. Set it in their player profile, then check again.` }
  }
  const levels = [player?.gameLevel?.doubles, partner?.gameLevel?.doubles]
  if (!isLevel(level) || !levels.every(isLevel)) return refused("This team does not fit this competition's level restrictions.")
  return refused(`This team does not fit Level ${level}: team average ${(levels[0] + levels[1]) / 2} (levels ${levels[0]} and ${levels[1]}). ${levelRange(level, "doubles", restrictions)}`)
}

// An admission refusal `code` as { text, recheck }, or null when `code` is not one. `recheck` when setting a missing level in
// the player profile lets the same selection fit.
const admission = (code, { gameType, level: competitionLevel, restrictions, player, partner, levelsRechecked } = {}) => {
  // The API compares Number(competition.level); an older competition can hold it as a string.
  const level = competitionLevel === null || competitionLevel === "" ? NaN : Number(competitionLevel)
  if (code === "TEAM_NOT_ALLOWED_AVG_LEVEL" || code === "TEAM_LEVEL_FORBIDDEN_TO_TEAMS") {
    return teamLevel({ level, restrictions, player, partner, levelsRechecked })
  }
  if (code === "TEAM_MUST_BE_MIXT") return refused("This competition accepts mixed teams only: the partners must be of different genders.")
  if (code === "DUPLICATE_TEAM_PARTICIPANT") return refused("Select two different players for the team.")
  const [, partnerPrefix, restriction] = /^(PARTNER_)?USER_NOT_ALLOWED_CASE_([A-Z_]+)$/.exec(code || "") || []
  if (!restriction) return null
  const person = partnerPrefix ? partner : player
  const name = fullName(person, partnerPrefix ? "The partner" : "The player")
  if (restriction === "LEVEL") {
    // As for a team: after Check again the search's level may be out of date.
    if (levelsRechecked) return { recheck: true, text: `${name} does not fit this competition's level.` }
    if (lacksLevel(person, gameType)) {
      return { recheck: true, text: `${name} has no ${gameType} level set, so this competition cannot admit them. Set it in their player profile, then check again.` }
    }
    const playerLevel = person?.gameLevel?.[gameType]
    return refused(isLevel(level) && isLevel(playerLevel)
      ? `${name} does not fit Level ${level}: ${gameType} level ${playerLevel}. ${levelRange(level, gameType, restrictions)}`
      : `${name} does not fit this competition's level.`)
  }
  return refused(`${name} ${restrictionText[restriction] || "does not meet this competition's restrictions."}`)
}

// The text for an admission refusal `code`, or null when `code` is not one.
export const admissionRefusal = (code, context) => admission(code, context)?.text ?? null

// The selected person a refusal can be read from, as the API checks it (Events entryManagement/service.js quote()).
const refusedFor = {
  INCOMPLETE_PARTICIPANT: person => !person?.birthDate || !Number.isFinite(new Date(person.birthDate).getTime()) || !person?.gender,
  PARTICIPANT_NOT_ACTIVE: person => person?.status !== "activated",
}

// Who a participant refusal is about: the person the API names by role, the only player in singles, the one partner
// the selection shows the cause for, or else either partner.
const refusedName = (code, { gameType, player, partner, participant }) => {
  if (participant === "partner") return fullName(partner, "The partner")
  if (participant === "payer" || gameType !== "doubles") return fullName(player, "The player")
  const suspects = refusedFor[code] ? [player, partner].filter(refusedFor[code]) : []
  if (suspects.length === 1) return suspects[0] === partner ? fullName(partner, "The partner") : fullName(player, "The player")
  const names = [player, partner].map(person => fullName(person, ""))
  return names.every(Boolean) ? names.join(" or ") : "One player"
}

const unsetFee = name => `The participation fee that applies to ${name} is not set for this competition. Complete every amount in the competition's Participation fees.`
const gone = { text: () => "This event or competition no longer exists. Reload the page." }
const unreadable = { text: () => "This selection could not be read. Close this window and select the players again." }
// A refusal the page's entry availability (read every 30 s, Events entryManagement/admission.js) or the actor's access had
// not caught up with, in entryMessage's words.
const stated = recheck => ({ recheck, text: (name, context, code) => entryMessage(code) })

const games = gameType => gameType === "doubles" ? ["doubles", "Doubles"] : ["singles", "Singles"]
const missingLimit = ({ gameType }) => `This event has no valid ${games(gameType)[1]} per player limit. Set it in the event details.`

// The event's entries-per-player limit (Singles/Doubles per player): undefined when the page has not read it. The API refuses
// with EVENT_ENTRY_LIMIT_REACHED when the event holds no integer limit.
const limitReached = (name, context) => {
  const { entryLimit } = context, [game, field] = games(context.gameType)
  if (entryLimit === undefined) {
    return `${name} has reached this event's ${game} entry limit per player. Withdraw one of them or raise ${field} per player in the event details.`
  }
  if (!Number.isInteger(entryLimit) || entryLimit < 1) return missingLimit(context)
  return entryLimit === 1
    ? `${name} already has 1 ${game} entry in this event, the limit per player. Withdraw it or raise ${field} per player in the event details.`
    : `${name} already has ${entryLimit} ${game} entries in this event, the limit per player. Withdraw one of them or raise ${field} per player in the event details.`
}

// Quote refusals that name their cause; `recheck` when fixing it elsewhere (a profile, the event details) lets the same selection fit.
const quoteTexts = {
  ALREADY_REGISTERED_TO_COMPETITION: { recheck: true, text: name => `${name} already has an entry or a registration in progress in this competition.` },
  EVENT_ENTRY_LIMIT_REACHED: { recheck: true, text: limitReached },
  EVENT_ENTRY_LIMIT_UNVERIFIED: { recheck: true, text: (name, context) => missingLimit(context) },
  INCOMPLETE_PARTICIPANT: { recheck: true,
    text: name => `${name}'s profile has no birth date or gender. Complete it in their player profile, then check again.` },
  PARTICIPANT_NOT_ACTIVE: { text: name => `${name}'s account is not active, so they cannot be registered.` },
  PARTICIPANT_SUSPENDED: { text: name => `${name} is suspended and cannot be registered.` },
  // A competition without fees takes no entry until they are saved (Events Planner ticket 25).
  COMPETITION_FEES_NOT_SET: { text: () => "This competition has no fees yet, so it takes no entry. Set the competition's fees first." },
  // Payments prices the entry in the payer's wallet currency (authorizePayment/validateRequest.js).
  COMPETITION_FEES_NOT_AVAILABLE: { text: (name, { player }) => `This competition has no participation fees in ${fullName(player, "the payer")}'s currency. Add fees for their country in the competition's Participation fees, or choose another payer.` },
  USER_INVALID_FEE_TAX: { text: (name, { player }) => unsetFee(fullName(player, "the payer")) },
  PARTNER_USER_INVALID_FEE_TAX: { text: (name, { partner }) => unsetFee(fullName(partner, "the partner")) },
  EVENT_NOT_FOUND: gone,
  COMPETITION_NOT_FOUND: gone,
  // Only Payments answers USER_NOT_FOUND, for the payer.
  USER_NOT_FOUND: { text: (name, { player }) => `${fullName(player, "The payer")}'s account could not be read. Close this window and select the player again.` },
  // Accounts no longer has a participant account the search listed.
  ACCOUNT_NOT_FOUND: unreadable,
  INVALID_PARTICIPANT: unreadable,
  INVALID_ENTRY_TYPE: unreadable,
  ENTRY_POINTS_ALREADY_SAVED: stated(false),
  ENTRY_MAIN_DRAW_GENERATED: stated(false),
  ERROR_PERMISSIONS: stated(false),
  // Reactivating the competition, or a WC place freed or added elsewhere, lets the same selection fit.
  COMPETITION_REACTIVATION_REQUIRED: stated(true),
  NO_MORE_WILDCARDS_AVAILABLE: stated(true),
}

// The text and follow-up for a quote refusal `code`, or null when the quote failure is not one the selection explains.
// REGISTRATION_ACCOUNT_UNVERIFIED, ENTRY_ADMISSION_UNVERIFIED, ENTRY_CAPACITY_UNVERIFIED and ENTRY_FEE_UNVERIFIED are left
// out: a read the API could not make can pass on retry.
export const quoteRefusal = (code, context = {}) => {
  const entry = quoteTexts[code]
  if (entry) return { text: entry.text(refusedName(code, context), context, code), recheck: Boolean(entry.recheck) }
  return admission(code, context)
}
